// Bulk delete all images from a draft's textBlocks

import { extractS3Key } from '@trilirivos/media-keys';
import { TextBlock } from '../interfaces/textEditor.interface';
import { deleteImageFromS3 } from './deleteImagefromS3';
import { extractImageUrlsFromTextBlocks } from './extractImageUrlsFromTextBlocks';

export const deleteAllImagesInDraft = async (
  textBlocks: TextBlock[],
): Promise<{
  success: string[];
  failed: Array<{ url: string; key: string; error: string }>;
}> => {
  // Extract all image URLs from textBlocks
  const imageUrls = extractImageUrlsFromTextBlocks(textBlocks);

  if (imageUrls.length === 0) {
    return { success: [], failed: [] };
  }

  const results = {
    success: [] as string[],
    failed: [] as Array<{ url: string; key: string; error: string }>,
  };

  // Process deletions with some parallelism but not too many concurrent requests
  const batchSize = 3; // Adjust based on your API limits

  for (let i = 0; i < imageUrls.length; i += batchSize) {
    const batch = imageUrls.slice(i, i + batchSize);

    const batchPromises = batch.map(async (url) => {
      try {
        // Extract the S3 key from the URL
        const imageKey = extractS3Key(url);

        if (!imageKey) {
          throw new Error('Could not extract image key from URL');
        }

        // Delete the image from S3
        await deleteImageFromS3(imageKey);

        return { success: true, url, key: imageKey };
      } catch (error) {
        const imageKey = extractS3Key(url);
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';

        console.error(`Failed to delete image ${url}:`, error);

        return {
          success: false,
          url,
          key: imageKey,
          error: errorMessage,
        };
      }
    });

    // Wait for current batch to complete
    const batchResults = await Promise.allSettled(batchPromises);

    // Process results
    batchResults.forEach((result) => {
      if (result.status === 'fulfilled') {
        if (result.value.success) {
          results.success.push(result.value.key as string);
        } else {
          results.failed.push({
            url: result.value.url,
            key: result.value.key as string,
            error: result.value.error as string,
          });
        }
      } else {
        // This shouldn't happen with our current setup, but just in case
        results.failed.push({
          url: 'unknown',
          key: 'unknown',
          error: result.reason?.message || 'Promise rejected',
        });
      }
    });
  }

  if (results.failed.length > 0) {
    console.error('Failed deletions:', results.failed);
  }

  return results;
};
